import {
  OrganizationType,
  SharingInvitationType,
} from "../../__generated__/graphql";
import CreateSharingInvitationModal from "../../components/CreateSharingInvitationModal";
import Button from "../../components/Button";
import {
  EnvelopeIcon,
  PlusIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import CollaboratorsList from "./components/CollaboratorsList";
import InvitationsList from "./components/InvitationsList";
import TabBar, { Tab } from "../../components/TabBar";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import PageLayout from "../../components/PageLayout";
import SectionEmptyState from "../../components/SectionEmptyState";
import useOrganizationSharings from "../../hooks/useOrganizationSharings";
import useCurrentOrgInvitations from "./components/InvitationsList/hooks/useCurrentOrgInvitations";
import { FaPlus } from "react-icons/fa6";

const tabs: Tab[] = [
  {
    label: "Collaborateurs actifs",
    value: "actives",
    icon: <UserIcon className="w-4 h-4" />,
  },
  {
    label: "Invitations",
    value: "invitations",
    icon: <EnvelopeIcon className="w-4 h-4" />,
  },
];

export default function Collaborators() {
  const { currentOrganization } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);

  const [invitationModalIsOpen, setInvitationModalIsOpen] = useState(false);

  // Fetch data to check if there are any collaborators or invitations
  const {
    data: collaboratorsData,
    loading: collaboratorsLoading,
    externalUserOrganizations,
  } = useOrganizationSharings();

  const { data: invitationsData, loading: invitationsLoading } =
    useCurrentOrgInvitations();

  const hasAnyCollaborators = useMemo(() => {
    const hasCollaborators =
      (collaboratorsData?.organizationSharings?.length ?? 0) > 0 ||
      (externalUserOrganizations?.length ?? 0) > 0;
    return hasCollaborators;
  }, [collaboratorsData?.organizationSharings, externalUserOrganizations]);

  const hasAnyInvitations = useMemo(() => {
    return (invitationsData?.sharingInvitations?.length ?? 0) > 0;
  }, [invitationsData?.sharingInvitations]);

  const hasAnyCollaboratorsOrInvitations = useMemo(() => {
    return hasAnyCollaborators || hasAnyInvitations;
  }, [hasAnyCollaborators, hasAnyInvitations]);

  useEffect(() => {
    // Set the tab to the one in the search params
    setActiveTab(
      tabs.find((t) => t.value === searchParams.get("tab")) ?? tabs[0]
    );
  }, [searchParams]);

  const handleTitleBasedOnOrganizationType = () => {
    if (!hasAnyCollaboratorsOrInvitations) {
      return "";
    }

    const organizationType = currentOrganization?.organizationType;

    if (!organizationType) {
      return "Collaborateurs";
    }

    if (organizationType === OrganizationType.Producer) {
      return "Artistes & Collab'";
    }

    if (organizationType === OrganizationType.Agency) {
      return "Talents";
    }

    return "Collaborateurs";
  };

  const handleButtonTextBasedOnOrganizationType = () => {
    const organizationType = currentOrganization?.organizationType;

    if (!organizationType) {
      return "Inviter un collaborateur";
    }

    if (organizationType === OrganizationType.Producer) {
      return "Collaborer avec une nouvelle personne";
    }

    if (organizationType === OrganizationType.Agency) {
      return "Inviter un talent";
    }

    return "Inviter un collaborateur";
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams({ tab: tab.value });
  };

  const tabComponent = () => {
    return (
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={(tab) => handleTabChange(tab)}
      />
    );
  };

  const emptyState = () => {
    return (
      <SectionEmptyState
        icon={<UserGroupIcon className="w-10 h-10 text-accent" />}
        title={handleTitleBasedOnOrganizationType()}
        description={
          <div>
            Invitez des{" "}
            <span className="text-accent font-medium">collaborateurs</span> et
            centralisez vos échanges : projets, évènements, documents, plannings
            — tout au même endroit.
          </div>
        }
        actionButton={
          <Button
            variant="primary"
            onClick={() => setInvitationModalIsOpen(true)}
          >
            <FaPlus className="w-5 h-5 mr-2" />
            {handleButtonTextBasedOnOrganizationType()}
          </Button>
        }
      />
    );
  };

  const pageContent = () => {
    // If there are no collaborators or invitations at all, show the empty state
    if (
      !hasAnyCollaboratorsOrInvitations &&
      !collaboratorsLoading &&
      !invitationsLoading
    ) {
      return emptyState();
    }

    return (
      <>
        {tabComponent()}
        {activeTab.value === "actives" && <CollaboratorsList />}
        {activeTab.value === "invitations" && <InvitationsList />}
      </>
    );
  };

  return (
    <PageLayout
      title={handleTitleBasedOnOrganizationType()}
      dataTestId="collaborators-page"
      subtitle={
        hasAnyCollaboratorsOrInvitations
          ? "Gérez ici les personnes avec lesquelles vous pouvez collaborer."
          : ""
      }
      actions={
        hasAnyCollaboratorsOrInvitations ? (
          <Button
            variant="secondary"
            onClick={() => setInvitationModalIsOpen(true)}
            dataTestId="invite-collaborator-button"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {handleButtonTextBasedOnOrganizationType()}
          </Button>
        ) : null
      }
    >
      <CreateSharingInvitationModal
        isModalOpen={invitationModalIsOpen}
        onClose={() => setInvitationModalIsOpen(false)}
        invitationType={SharingInvitationType.ExternalMember}
      />

      {pageContent()}
    </PageLayout>
  );
}
